/**
 * Shapes served by the call analytics service through core's /api/v2/call-analytics proxy.
 * Mirrors techladder-call-analytics docs/RESULTS.md (schema_version 2) and docs/CONTRACT.md.
 * `null` always means "not measured", never zero.
 */

export const FEATURE_KEYS = ["acoustic", "emotion", "sentiment", "qa", "objection", "cx"] as const;
export type FeatureKey = (typeof FEATURE_KEYS)[number];

export type FeatureRunStatus = "completed" | "skipped" | "failed";
export type SentimentLabel = "positive" | "neutral" | "negative";
export type Speaker = "agent" | "contact";
export type Level = "low" | "medium" | "high";

// ── Calls ───────────────────────────────────────────────────────────────

export interface CallHeadline {
  qa_score: number | null;
  qa_passed: boolean | null;
  sentiment_score: number | null;
  sentiment_label: SentimentLabel | null;
  cx_score: number | null;
  escalation_risk: Level | null;
  risk_score: number | null;
  objection_count: number | null;
  dominant_emotion: string | null;
}

export interface CallSummary {
  call_id: string;
  agent_id: string | null;
  agent_name: string | null;
  call_at: string;
  duration_s: number | null;
  features: Partial<Record<FeatureKey, FeatureRunStatus>>;
  headline: Partial<CallHeadline>;
}

export interface CallsPage {
  items: CallSummary[];
  next_cursor: string | null;
}

export interface Turn {
  index: number;
  speaker: Speaker;
  text: string;
  start: number | null;
  end: number | null;
  playback: "complete" | "interrupted" | "mid_call_hangup" | null;
}

export interface FeatureOutcome<R> {
  status: FeatureRunStatus;
  reason: string | null;
  result: R | null;
  updated_at: string | null;
}

export interface FeatureResults {
  acoustic: AcousticResult;
  emotion: EmotionResult;
  sentiment: SentimentResult;
  qa: QaResult;
  objection: ObjectionResult;
  cx: CxResult;
}

/** `call` in GET /calls/{id}: the call itself, whether or not it was analysed yet. */
export interface CallInfo {
  call_id: string;
  agent_id: string | null;
  agent_name: string | null;
  call_at: string | null;
  duration_s: number | null;
  pca_sentiment: string | null;
  analysed: boolean;
}

export interface CallDetail {
  call: CallInfo;
  features: { [K in FeatureKey]?: FeatureOutcome<FeatureResults[K]> };
  turns: Turn[];
}

// ── acoustic ────────────────────────────────────────────────────────────

export interface SpeakerAcoustics {
  talk_time_s: number | null;
  talk_ratio: number | null;
  speaking_rate_wpm: number | null;
  avg_pitch_hz: number | null;
  pitch_variability_hz: number | null;
  avg_energy_db: number | null;
  energy_variability_db: number | null;
  jitter_pct: number | null;
  shimmer_pct: number | null;
}

export type DeliveryChangeKind = "pitch_rise" | "pitch_drop" | "energy_rise" | "energy_drop" | "rate_up" | "rate_down";

export interface AcousticResult {
  duration_s: number | null;
  speakers: Partial<Record<Speaker, SpeakerAcoustics | null>>;
  pauses: { count: number | null; avg_ms: number | null; long_count: number | null; longest_ms: number | null; per_minute: number | null } | null;
  turns: Array<{
    turn_index: number; speaker: Speaker; start: number | null; end: number | null;
    rate_wpm: number | null; pitch_hz: number | null; energy_db: number | null; pause_before_ms: number | null;
  }>;
  emphasis: Array<{ turn_index: number; speaker: Speaker; at_s: number | null; energy_db: number | null; above_mean_db: number | null }>;
  delivery_changes: Array<{ turn_index: number; speaker: Speaker; kind: DeliveryChangeKind; delta: number | null }>;
  quality_score: number | null;
  quality_deductions: Array<{ reason: string; points: number }>;
  availability: { timing: boolean; pitch_energy: boolean; aligned_turns: number | null; asr_model: string | null; reason: string | null };
}

// ── emotion ─────────────────────────────────────────────────────────────

export interface EmotionSide {
  dominant: string | null;
  customer_dominant: string | null;
  distribution: Record<string, number>;
  model: string | null;
  prompt_version?: string | null;
}

export interface EmotionResult {
  text: EmotionSide | null;
  audio: EmotionSide | null;
  turns: Array<{
    turn_index: number; speaker: Speaker; start: number | null; end: number | null;
    text: { primary: string | null; scores: Record<string, number>; confidence: number | null } | null;
    audio: { primary: string | null; scores: Record<string, number>; valence: number | null; arousal: number | null } | null;
    speaking_pattern: { duration_s: number | null; words_per_minute: number | null; pause_before_s: number | null } | null;
  }>;
  availability: { text: boolean; audio: boolean; audio_reason: string | null; text_reason?: string | null; timing: boolean };
}

// ── sentiment ───────────────────────────────────────────────────────────

export interface SentimentResult {
  overall: {
    score: number | null; label: SentimentLabel | null; confidence: number | null;
    text_score: number | null; audio_score: number | null; agreement: Level | null;
  } | null;
  trend: {
    direction: "improving" | "deteriorating" | "stable" | string; delta: number | null;
    start_score: number | null; end_score: number | null; turning_points: number[];
  } | null;
  segments: Array<{ part: "opening" | "middle" | "closing" | string; score: number | null }>;
  turns: Array<{
    turn_index: number; start: number | null; end: number | null; score: number | null; label: SentimentLabel | null;
    confidence: number | null; text_score: number | null; audio_score: number | null; key_phrases: string[];
  }>;
  pca_label: SentimentLabel | null;
  availability: { text: boolean; audio: boolean; audio_reason: string | null };
}

// ── qa ──────────────────────────────────────────────────────────────────

export interface QaResult {
  score: number | null;
  pass_threshold: number | null;
  passed: boolean | null;
  criteria: Array<{
    id: string; label: string; weight: number; passed: boolean | null; evidence: string | null; reason: string | null;
  }>;
  triggers: {
    repetition?: Array<{ turn_index: number; repeats_turn: number; similarity: number; quote: string }>;
    dead_air?: Array<{ turn_index: number; duration_s: number }>;
    false_claims?: Array<{ claim: string; quote: string; turn_index: number | null }>;
    supervision?: Array<{ type: string; severity: string; detail: string; turn_index: number | null }>;
  };
  penalty: number | null;
  scorecard: { source: string; updated_at: string | null } | null;
  prompt_used: boolean;
  rationale: string | null;
  availability: { timing: boolean; agent_prompt: boolean; supervision_alerts: boolean };
}

// ── objection ───────────────────────────────────────────────────────────

export interface ObjectionResult {
  objections: Array<{
    type: string; quote: string; turn_index: number | null; resolved: boolean | null;
    response_quality: string | null; confidence: number | null;
  }>;
  competitors: Array<{ name: string; quote: string; turn_index: number | null; source: string }>;
  compliance: {
    prohibited_claims: Array<{ claim: string; quote: string; turn_index: number | null; severity: Level; source: string }>;
    missing_disclosures: Array<{ disclosure: string; source: string }>;
    policy_violations: Array<{ rule: string; quote: string; turn_index: number | null; severity: Level }>;
  };
  escalations: Array<{ kind: string; quote: string; turn_index: number | null }>;
  risk_score: number | null;
  availability: { agent_prompt: boolean };
}

// ── cx ──────────────────────────────────────────────────────────────────

export interface CxResult {
  cx_score: number | null;
  effort: { score: number | null; signals: Array<{ signal: string; quote: string; turn_index: number | null }> } | null;
  satisfaction: { level: string | null; indicators: Array<{ signal: string; polarity: string; quote: string }> } | null;
  unresolved_concerns: Array<{ concern: string; quote: string }>;
  friction_points: Array<{ point: string; quote: string; turn_index: number | null }>;
  escalation_risk: { level: Level | null; score: number | null; factors: string[] } | null;
  follow_up_required: boolean | null;
  summary: string | null;
  suggestions: Array<{
    audience: "ai_agent" | "human_agent"; priority: "high" | "medium" | "low"; action: string;
    source_feature: string | null; evidence_count: number | null;
  }>;
  inputs: { used: string[]; missing: string[] };
  availability: { llm: boolean };
}

// ── summary ─────────────────────────────────────────────────────────────

export interface AnalyticsSummary {
  from: string;
  to: string;
  /** by_day buckets use this zone. */
  timezone: string;
  calls: number;
  features: Partial<Record<FeatureKey, Partial<Record<FeatureRunStatus, number>>>>;
  qa: { avg_score: number | null; pass_rate: number | null } | null;
  sentiment: { avg_score: number | null; distribution: Partial<Record<SentimentLabel, number>> } | null;
  cx: { avg_score: number | null; high_risk_calls: number | null } | null;
  objections: Record<string, number>;
  emotions: Record<string, number>;
  by_day: Array<{ date: string; calls: number; avg_qa: number | null; avg_sentiment: number | null }>;
}

// ── configuration ───────────────────────────────────────────────────────

export interface FeatureSetting {
  enabled: boolean | null;
  sample_pct: number | null;
}

export type FeatureSettings = Partial<Record<FeatureKey, FeatureSetting>>;

export interface ScopeDoc {
  scope: "platform" | "organization" | "agent";
  scope_id: string;
  features: FeatureSettings;
  updated_by?: string | null;
  updated_at?: string | null;
}

export interface EffectiveFeature {
  enabled: boolean;
  sample_pct: number;
  decided_by: string;
}

export type EffectiveConfig = Partial<Record<FeatureKey, EffectiveFeature>>;

export interface ConfigResponse {
  organization_id: string;
  master_enabled: boolean;
  can_configure: boolean;
  platform: ScopeDoc | null;
  organization: ScopeDoc | null;
  agents: ScopeDoc[];
  effective: EffectiveConfig;
}

export interface AgentConfigResponse {
  agent_id: string;
  override: FeatureSettings | null;
  effective: EffectiveConfig;
}

export interface FeatureInfo {
  key: FeatureKey;
  label: string;
  description: string;
  requires: string[];
}

export interface ScorecardCriterion {
  id: string;
  label: string;
  description: string;
  weight: number;
}

export interface Scorecard {
  criteria: ScorecardCriterion[];
  pass_threshold: number;
  competitors: string[];
  prohibited_claims: string[];
  required_disclosures: string[];
}

/** The scorecard in force and which level it came from; an agent's wins whole. */
export interface EffectiveScorecard extends Scorecard {
  source: "default" | "organization" | "agent" | string;
  updated_at: string | null;
}

export interface ScorecardResponse {
  effective: EffectiveScorecard;
  organization: (Scorecard & { updated_at?: string | null }) | null;
  agent: (Scorecard & { updated_at?: string | null }) | null;
}

// ── improvements ────────────────────────────────────────────────────────

export type ImprovementStatus = "queued" | "running" | "completed" | "failed";

export interface ImprovementRun {
  run_id: string;
  agent_id: string;
  status: ImprovementStatus;
  requested_by: string | null;
  created_at: string;
  updated_at: string;
  shortcomings: Array<{ key: string; label: string; count: number; share: number | null; examples: string[] }> | null;
  proposal: {
    base_version_id: string;
    changes: Array<{ old: string; new: string; reason: string }>;
    candidate_prompt: string;
    rationale: string | null;
  } | null;
  evaluation: {
    cases: number;
    baseline_pass_rate: number | null;
    candidate_pass_rate: number | null;
    regressions: string[];
    fixed: string[];
    passed: boolean;
    reason: string | null;
    case_results: Array<{
      call_id: string; baseline: boolean | null; candidate: boolean | null;
      baseline_reason: string | null; candidate_reason: string | null;
    }>;
  } | null;
  error: string | null;
}
